import { tierConf } from "core/model/config";
import type { Address, AlgoliaAutocompleteOption } from "core/types";
import { merge } from "lodash";
import { stringify } from "querystring";
import { useEffect, useState } from "react";
import { useCountry, useGetLanguage } from "reduxentities/hooks";

export type MapboxSupportedLanguage = "de" | "en" | "fr" | "it" | "nl";

type PartialMapboxResult = {
  features: {
    address?: string;
    center: number[];
    context: { id: string; text: string }[];
    geometry: { coordinates: number[]; type: string };
    id: string;
    place_name: string;
    place_type: string[];
    properties: { accuracy: string };
    relevance: number;
    text: string;
    type: string;
  }[];
};

export type MapboxGeocodedAddress = {
  id: string;
  city: string;
  country: string;
  house_number: string;
  latitude: string;
  longitude: string;
  region: string;
  street: string;
  zipcode: string;
};

export type MapboxAddressAutocompleteOption = AlgoliaAutocompleteOption & {
  address: MapboxGeocodedAddress;
};

export type MapboxFilters = {
  autocomplete?: boolean;
  bbox?: `${number},${number},${number},${number}`;
  country?: string;
  language?: MapboxSupportedLanguage;
  limit?: number;
};

type PropertyMap = Record<
  | "country"
  | "house_number"
  | "latitude"
  | "longitude"
  | "place"
  | "postcode"
  | "region"
  | "street",
  keyof Omit<MapboxGeocodedAddress, "id">
>;

export const DEFAULT_MAPBOX_FILTERS: MapboxFilters = {
  autocomplete: true,
  limit: 5,
};

const DEFAULT_PROPERTY_MAP: PropertyMap = {
  country: "country",
  house_number: "house_number",
  latitude: "latitude",
  longitude: "longitude",
  place: "city",
  postcode: "zipcode",
  region: "region",
  street: "street",
};

const excludedFromOptionLabel: (keyof MapboxGeocodedAddress)[] = [
  "latitude",
  "longitude",
  "id",
];

function reverseString(str: string) {
  return str.split("").reverse().join("");
}

export function getMapboxAddressOptionLabel(
  option: MapboxGeocodedAddress | string,
): string {
  let result = "";

  if (!option) return result;

  if (typeof option === "string") {
    return option;
  }

  Object.values(DEFAULT_PROPERTY_MAP).forEach((field) => {
    if (
      option[field] &&
      !excludedFromOptionLabel.includes(field as keyof MapboxGeocodedAddress)
    ) {
      result += `${option[field]} `;
    }
  });

  return result.trim();
}

export function formatMapboxResult(
  result: PartialMapboxResult | undefined,
  propertyMap: PropertyMap = DEFAULT_PROPERTY_MAP,
): MapboxGeocodedAddress[] {
  let addresses: MapboxGeocodedAddress[] = [];

  if (result?.features) {
    addresses = result.features.map((item) => {
      const [longitude, latitude] = item.center;

      let street = item.place_name.split(",")?.[0] ?? "";

      const houseNumber = item.address ?? "";

      if (houseNumber) {
        street = reverseString(
          reverseString(street).replace(`${reverseString(houseNumber)} `, ""),
        );
      }

      const otherProps = item?.context?.reduce(
        (acc: Partial<MapboxGeocodedAddress>, { id, text }) => {
          const propertyName = id.split(".")?.[0];
          if (propertyName in propertyMap) {
            const value = text ?? "";
            return {
              ...acc,
              [propertyMap[propertyName as keyof PropertyMap]]: value,
            };
          }

          return { ...acc };
        },
        {} as Partial<MapboxGeocodedAddress>,
      );

      return {
        id: item.id,
        [propertyMap["house_number"]]: houseNumber,
        [propertyMap["latitude"]]: String(latitude),
        [propertyMap["longitude"]]: String(longitude),
        [propertyMap["street"]]: street,
        ...otherProps,
      } as MapboxGeocodedAddress;
    });
  }

  return addresses;
}

export function toMapboxAutocompleteOption(
  address: MapboxGeocodedAddress,
): MapboxAddressAutocompleteOption {
  const label = getMapboxAddressOptionLabel(address);
  return {
    address,
    id: address.id,
    label,
    value: address.id,
  };
}

export function mapboxGeocodedAddressToCoreAddress(
  row: MapboxGeocodedAddress,
): Address {
  const lat = parseFloat(String(row.latitude));
  const lng = parseFloat(String(row.longitude));
  return {
    city: row.city,
    country: row.country,
    latitude: Number.isFinite(lat) ? lat : undefined,
    longitude: Number.isFinite(lng) ? lng : undefined,
    street: row.street,
    street_number: row.house_number || undefined,
    zipcode: row.zipcode,
  };
}

export function mapboxAutocompleteOptionToCoreAddress(
  option: MapboxAddressAutocompleteOption | null,
): Address | null {
  if (!option?.address) {
    return null;
  }
  return mapboxGeocodedAddressToCoreAddress(option.address);
}

export function useMapboxSearchParams({
  mapboxFilters,
}: {
  mapboxFilters?: MapboxFilters;
}) {
  const country = useCountry();
  const language = useGetLanguage();
  const token = tierConf.production.mapbox;

  const mergedFilters = merge(DEFAULT_MAPBOX_FILTERS, {
    ...merge({ country, language, ...mapboxFilters }),
    access_token: token,
    types: "address",
  });

  const [filters] = useState(mergedFilters);
  return stringify(filters);
}

/**
 * Mapbox forward-geocode search. Debounce query updates in the UI layer
 * (e.g. {@link MapboxAddressAutocomplete}).
 */
export function useMapboxGeocodeQuery({
  mapboxFilters,
  searchText,
}: {
  mapboxFilters?: MapboxFilters;
  searchText: string | null;
}) {
  const [error, setError] = useState<Error | undefined>();
  const searchParams = useMapboxSearchParams({ mapboxFilters });
  const [options, setOptions] = useState<MapboxGeocodedAddress[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    let controller: AbortController | null = new AbortController();

    (async () => {
      setError(undefined);
      if (searchText == null || searchText.trim() === "") {
        setOptions([]);
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const encoded = encodeURIComponent(searchText);
        const response = await fetch(
          `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?${searchParams}`,
          { signal: controller.signal },
        );
        const data: PartialMapboxResult | undefined = await response.json();
        setOptions(formatMapboxResult(data));
        controller = null;
      } catch (err) {
        if (!controller?.signal.aborted) {
          setError(err as Error);
        }
      } finally {
        if (!controller?.signal.aborted) {
          setIsLoading(false);
        }
      }
    })();

    return () => controller?.abort();
  }, [searchParams, searchText]);

  return { error, isLoading, options };
}

export const DEFAULT_MAPBOX_INPUT_DEBOUNCE_MS = 350;

/** @deprecated Prefer {@link useMapboxGeocodeQuery} for new ds/ui wiring. */
export function useMapbox({
  defaultValue,
  mapboxFilters,
}: {
  defaultValue: MapboxGeocodedAddress | null;
  mapboxFilters?: MapboxFilters;
}) {
  const [error, setError] = useState<Error | undefined>();
  const searchParams = useMapboxSearchParams({ mapboxFilters });
  const [searchText, setSearchText] = useState<string | null>();
  const [selectedOption, setSelectedOption] =
    useState<MapboxGeocodedAddress | null>(defaultValue);
  const [options, setOptions] = useState<MapboxGeocodedAddress[]>([]);

  useEffect(() => {
    let controller: AbortController | null = new AbortController();
    (async () => {
      setError(undefined);
      try {
        if (searchText != null) {
          const encoded = encodeURIComponent(searchText);
          const response = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encoded}.json?${searchParams}`,
            {
              signal: controller.signal,
            },
          );
          const data: PartialMapboxResult | undefined = await response.json();
          setOptions(formatMapboxResult(data));
          controller = null;
        }
      } catch (err) {
        if (!controller?.signal.aborted) {
          setError(err as Error);
        }
      }
    })();

    return () => controller?.abort();
  }, [searchText, searchParams]);

  return {
    error,
    options,
    searchText,
    selectedOption,
    setSearchText,
    setSelectedOption,
  };
}
